// library imports
import React, { useEffect, useState, useRef  } from "react";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Button, Row, Stack, Card, Pagination, WordCloudView, List, Icon, TextInput, ProgressBar, LoadingBox} from "_core/components/common/UI";
import { CustomDateRangePicker, DateRadio, FitList} from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";
import { ConvertMarkdownToHTML, isVisibleField } from "_core/utils/common";
import { useUpdateLock } from "_core/hooks/useUpdateLock";
import AdminUserChatLogInfoApi from "apis/AdminUserChatLogInfoApi";
import AdminGetLogAnalyticsApi from "apis/AdminGetLogAnalyticsApi";
import AdminLogAnalyticsFaqApi from "apis/AdminLogAnalyticsFaqApi";
import AdminLogAnalyticsFaqCountApi from "apis/AdminLogAnalyticsFaqCountApi";
import LogsCollectApi from "apis/LogsCollectApi";
import { InfoCard } from 'components/common/parts';
import Encoding from 'encoding-japanese';

// styles imports
import './AdminUserChatLog.scss';

const FilterLabel = ({ label, onClose }) => {
    return (
        <div className="filter-label">
            <span className="filter-label-text">{label}</span>
            <div className="filter-label-close" onClick={onClose}>
                <Icon icon="x-lg" size={10} />
            </div>
        </div>
    );
};

// チャットログ一覧のカラム定義を生成する
// FitList.jsが Object.values(columsize) の添字順で幅を割り当てるため、
// columsize / title / 各行のdata の3つでキーの順序が一致している必要がある。
// 定義が2箇所に散ると必ず食い違うため、ここに一元化する。
const CreateChatLogListSchema = (canViewAdjustQuestion) => ({
    columsize: {
        ip_address:1, question:3, answer:3, regdate:1
    },
    title: {
        ip_address: "IP / セッションID",
        question: canViewAdjustQuestion ? "質問 / 質問補強" : "質問",
        answer: "返答",
        regdate: "日時",
    },
    value: []
});

const AdminUserChatLog = () => {
    const dispatch = useDispatch();
    const ExecAdminUserChatLogInfoApi = AdminUserChatLogInfoApi();
    const ExecAdminGetLogAnalyticsApi = AdminGetLogAnalyticsApi();
    const CallAdminLogAnalyticsFaqApi = AdminLogAnalyticsFaqApi();
    const CallAdminLogAnalyticsFaqCountApi = AdminLogAnalyticsFaqCountApi();
    const CallLogsCollectApi = LogsCollectApi();
    const { tUser, tUserAdd, tAccount } = useSelector((store) => store.transaction);
    const { iConstantList } = useSelector((store) => store.master);
    const { initializeState } = useSelector((store) => store.scene);

    // 質問補強の閲覧権限
    // ※tAccountはリロード直後null（AdminReloadApi完了までセットされない）ためnullガード必須
    const canViewAdjustQuestion = isVisibleField(tAccount?.auth, Constants.FIELD_ADJUST_QUESTION);

    // デモユーザーと読み取り専用権限は、どちらも更新系を使わせないためまとめて扱う
    // このページでは分析の実行だけが更新系で、検索・フィルタ・CSVダウンロードは対象外
    const { isUpdateLocked } = useUpdateLock();

    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsFirstLoaded, setAnalyticsFirstLoaded] = useState(false);

    // よくある質問分析モーダル用
    const [isShowFaqModal, setIsShowFaqModal] = useState(false);
    const [isFaqGenerating, setIsFaqGenerating] = useState(false);
    const [faqRawText, setFaqRawText] = useState("");
    const [faqList, setFaqList] = useState([]);
    const [faqHtml, setFaqHtml] = useState("");
    const [faqInquiryCount, setFaqInquiryCount] = useState(0);

    // よくある質問分析の事前設定ポップアップ用
    const [isShowFaqConfigModal, setIsShowFaqConfigModal] = useState(false);
    const [faqConfigDateRadio, setFaqConfigDateRadio] = useState(Constants.RANGE_TYPE_SHORT);
    const [faqConfigStartDate, setFaqConfigStartDate] = useState(null);
    const [faqConfigEndDate, setFaqConfigEndDate] = useState(null);
    const [faqConfigDispCustomDate, setFaqConfigDispCustomDate] = useState(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
    const [faqConfigIsDialogVisible, setFaqConfigIsDialogVisible] = useState(false);
    const faqConfigDialogRef = useRef(null);
    // カレンダーで日付選択中に「外クリック/キャンセル」で巻き戻すためのスナップショット
    const faqConfigSnapshotRef = useRef({ start: null, end: null, disp: null });
    const [faqCountInPeriod, setFaqCountInPeriod] = useState(0);
    const [faqAnalysisLimit, setFaqAnalysisLimit] = useState(2000);
    const [isFaqCountLoading, setIsFaqCountLoading] = useState(false);

    const RangeLabelTypeKeys= Object.keys(Constants.RangeLabelType)
    const DispRangeLabelTypeValues= Object.values(Constants.RangeLabelType).filter(value => value !== Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM])

    // 表示期間ラジオボタン
    const [dateRadioCheck, setDateRadioCheck] = useState(Constants.RANGE_TYPE_SHORT)
    // 日付カスタムラジオボタン表示内容
    const [dispCustomDate, setDispCustomDate] = useState(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
    // 日付カスタムラジオボタンは文字が変動するため変数に格納しておく
    DispRangeLabelTypeValues.push(dispCustomDate)

    // 触っている日付範囲の制御
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    useEffect(() => {
        setStartDate(dateRange[0])
        setEndDate(dateRange[1])
    }, [dateRange]);

    // 最終API実行対象日付範囲
    const [execApiStartDate, setExecApiStartDate] = useState(null);
    const [execApiEndDate, setExecApiEndDate] = useState(null);
    const [words, setWords] = useState(null);

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [filterParams, setFilterParams] = useState({
        ip_address: "",
        session_id: "",
        question: "",
        answer: "",
        adjust_question: ""
    });
    const [editFilterParams, setEditFilterParams] = useState({
        ip_address: "",
        session_id: "",
        question: "",
        answer: "",
        adjust_question: ""
    });
    const [filterInputsKey, setFilterInputsKey] = useState(0);
    
    const [isListLoading, setIsListLoading] = useState(false);
    const [maxTalkCount, setMaxTalkCount] = useState(0);

    // 表示終了日は当日
    const maxDate = new Date();
    // 表示開始日はその一年前の日付
    const minDate = new Date(maxDate);
    minDate.setFullYear(maxDate.getFullYear() - 1);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isDownloadCsv, setIsDownloadCsv] = useState(false);

    // カレンダー設定
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const dialogRef = useRef(null);
    const radioLabelOptions = { month: 'long', day: 'numeric' };

    const DEFAULT_PAGE = 1
    const DEFAULT_LIMIT_FLG = 1
    const DEFAULT_ANALYTICS_LIMIT_FLG = 0

    const COLOR_DICT = {
        0   : "#B3DCFE",
        10  : "#BBC4F1",
        20  : "#C6A8E1",
        30  : "#CB98D8",
        40  : "#D383CC",
        50  : "#DB6CBF",
        60  : "#E356B3",
        70  : "#EB41A7",
        80  : "#F22D9C",
        90  : "#FF0B88",
    }

    // チャットログリストデータ
    // ※初期値は権限未確定(tAccount=null)の瞬間に評価されるため、必ず「見せない」側で初期化する
    const [chatLogListData, setChatLogListData] = useState(() => CreateChatLogListSchema(false));
    // チャットログリストデータ
    const [wordCountData, setWordCountData] = useState({
        columsize: {
            word:3, count:1,  graph:2
        },
        title: {
            word: "単語",
            count: "回数",
            graph: "",
        },
        value: []
    });

    useEffect(() => {
        if (!tUser) {
            return;
        }

        setMaxTalkCount(tUser.config.talk_limit);
    }, [tUser]);

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)
        {
            return
        }

        AdminUserChatLogInfoApiRequest(currentPage, DEFAULT_LIMIT_FLG, startDate, endDate)
    }, [words]);

    useEffect(() => {
        if(initializeState !== Constants.INITIALIZE_STATE.allComplete)
        {
            return
        }

        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetching))

        // 表示対象日付の設定
        const { nextStartDate, nextEndDate } = SetRangeByTargetDay(dateRadioCheck)

        // API実行
        setAnalyticsLoading(true)
        AdminGetLogAnalyticsApiRequest(DEFAULT_PAGE, DEFAULT_ANALYTICS_LIMIT_FLG, nextStartDate, nextEndDate)

        // ログリストAPIも実行
        AdminUserChatLogInfoApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, nextStartDate, nextEndDate, false, true)
        
    }, [initializeState]);

    useEffect(() => {
        setIsDownloadCsv(chatLogListData.value.length > 0)
    }, [chatLogListData]);

    // 権限確定後にヘッダー表記だけを追従させる（tAccountはリロード直後nullのため）
    // ※valueは絶対に作り直さないこと（作り直すと取得済みの一覧が消える）
    useEffect(() => {
        setChatLogListData((prev) => ({
            ...prev,
            title: CreateChatLogListSchema(canViewAdjustQuestion).title,
        }));
    }, [canViewAdjustQuestion]);

    useEffect(() => {
        if (isDialogVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDialogVisible]);

    // カレンダーの外側をクリック
    const handleClickOutside = (event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) 
        {
            setIsDialogVisible(false);
            setDateRange([startDate, endDate]); 
            if(startDate && endDate)
            {
                setDispCustomDate(startDate.toLocaleDateString('ja-JP', radioLabelOptions)+"-"+endDate.toLocaleDateString('ja-JP', radioLabelOptions))
            }
        }
    };
    
    // カレンダーから表示日付範囲一時変更
    const handleDateChange = (update) => {
        // startDateとendDateを一度に更新
        setDateRange(update); 
    };

    // カレンダーでキャンセル
    const handleCancel = () => {
        // 日付選択をリセット
        setDateRange([null, null]); 
    };
    
    // カレンダーから表示日付範囲変更確定
    const handleUpdate = () => {
        // カレンダーを非表示
        setIsDialogVisible(false);
        // カスタムラジオボタンに表示する日付更新
        setDispCustomDate(startDate.toLocaleDateString('ja-JP', radioLabelOptions)+"-"+endDate.toLocaleDateString('ja-JP', radioLabelOptions))
        // API実行
        setAnalyticsLoading(true)
        AdminGetLogAnalyticsApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, startDate, endDate)
    };

    // 指定日数間をデータにセット
    function SetRangeByTargetDay(targetDay)
    {
        // 取得必要日数の調整のため、マイナス１する
        targetDay = Number(targetDay)-1

        // 現在の日付を取得
        const nextEndDate = new Date();
        // 今日の日付を計算
        nextEndDate.setDate(nextEndDate.getDate());
        // 指定された日数分前の日付を計算
        const nextStartDate = new Date(nextEndDate);
        nextStartDate.setDate(nextEndDate.getDate() - targetDay);

        // 日付範囲を設定
        setDateRange([nextStartDate, nextEndDate]);

        return {
            nextStartDate: nextStartDate,
            nextEndDate: nextEndDate,
        };
    }

    const AdminUserChatLogInfoApiRequest = async (page, is_limit, nextStartDate, nextEndDate, isScroll=false, forceUpdate=false, overrideFilters = null) => {
        setIsListLoading(true)
        const tmpPage=currentPage
        setCurrentPage(page);

        // Check if dates are provided, fallback to state
        if (!nextStartDate || !nextEndDate) {
            nextStartDate = startDate;
            nextEndDate = endDate;
        }

        // Safety check: if still null, abort or handle appropriately
        if (!nextStartDate || !nextEndDate) {
            console.warn("Date range is null in AdminUserChatLogInfoApiRequest");
            setIsListLoading(false);
            return;
        }

        // リクエスト値をチェック
        let startDateStr = ""
        let nextStartDateStr = ""
        let endDateStr = ""
        let nextEndDateStr = ""

        if (execApiStartDate)
        {
            startDateStr = execApiStartDate
        }
        if (nextStartDate)
        {
            nextStartDateStr = nextStartDate.toLocaleDateString('ja-JP', radioLabelOptions) 
        }
        if (execApiEndDate)
        {
            endDateStr = execApiEndDate
        }
        if (nextEndDate)
        {
            nextEndDateStr = nextEndDate.toLocaleDateString('ja-JP', radioLabelOptions)
        }

        // 変化が無い場合はAPI実行しない
        if (! forceUpdate)
        {
            if (startDateStr == nextStartDateStr && endDateStr == nextEndDateStr)
            {
               // return
            }
        }

        setExecApiStartDate(nextStartDate ? nextStartDate.toISOString() : "")
        setExecApiEndDate(nextEndDate ? nextEndDate.toISOString() : "")

        // リクエスト値の設定
        const range_start_year = nextStartDate.getFullYear();
        const range_start_month = nextStartDate.getMonth() + 1;
        const range_start_day = nextStartDate.getDate();
        const range_end_year = nextEndDate.getFullYear();
        const range_end_month = nextEndDate.getMonth() + 1;
        const range_end_day = nextEndDate.getDate();

        // Use overrideFilters if provided, otherwise use current state
        const currentFilters = overrideFilters || filterParams;

        // 通信中の表示を出す
        dispatch(setLoading(true));
        const result = await ExecAdminUserChatLogInfoApi({
            "page":page,
            "is_limit":is_limit,
            "range_start_year": range_start_year,
            "range_start_month": range_start_month, 
            "range_start_day": range_start_day, 
            "range_end_year": range_end_year,
            "range_end_month": range_end_month, 
            "range_end_day": range_end_day,
            "ip_address": currentFilters.ip_address,
            "session_id": currentFilters.session_id,
            "question": currentFilters.question,
            "answer": currentFilters.answer,
            "adjust_question": canViewAdjustQuestion ? currentFilters.adjust_question : "",
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            setCurrentPage(tmpPage);
            setIsListLoading(false);
            CreateChatLogListData([]);
            setTotalPages(1);
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
        }
        else 
        {
            setIsListLoading(false)
            CreateChatLogListData(result.response.t_chat_log_list)
            setTotalPages(result.response.max_page)
            if (isScroll)
            {
                scrollToRef(listRef)
            }
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    const AdminGetLogAnalyticsApiRequest = async (page, is_limit, nextStartDate, nextEndDate, overrideFilters = null) => {
        console.log("情報取得実行");
        const tmpPage=currentPage
        setCurrentPage(page);
        // リクエスト値をチェック
        let startDateStr = ""
        let nextStartDateStr = ""
        let endDateStr = ""
        let nextEndDateStr = ""

        if (execApiStartDate)
        {
            startDateStr = execApiStartDate
        }
        if (nextStartDate)
        {
            nextStartDateStr = nextStartDate.toLocaleDateString('ja-JP', radioLabelOptions) 
        }
        if (execApiEndDate)
        {
            endDateStr = execApiEndDate
        }
        if (nextEndDate)
        {
            nextEndDateStr = nextEndDate.toLocaleDateString('ja-JP', radioLabelOptions)
        }

        // 変化が無い場合はAPI実行しない
        if (startDateStr == nextStartDateStr && endDateStr == nextEndDateStr) {
            if (!overrideFilters) return
        }

        setExecApiStartDate(nextStartDate.toISOString())
        setExecApiEndDate(nextEndDate.toISOString())

        // リクエスト値の設定
        const range_start_year = nextStartDate.getFullYear();
        const range_start_month = nextStartDate.getMonth() + 1;
        const range_start_day = nextStartDate.getDate();
        const range_end_year = nextEndDate.getFullYear();
        const range_end_month = nextEndDate.getMonth() + 1;
        const range_end_day = nextEndDate.getDate();

        // Use overrideFilters if provided, otherwise use current state
        const currentFilters = overrideFilters || filterParams;

        // 通信中の表示を出す
        dispatch(setLoading(true));
        const result = await ExecAdminGetLogAnalyticsApi({
            "page":page,
            "is_limit":is_limit,
            "range_start_year": range_start_year,
            "range_start_month": range_start_month, 
            "range_start_day": range_start_day, 
            "range_end_year": range_end_year,
            "range_end_month": range_end_month, 
            "range_end_day": range_end_day,
            "ip_address": currentFilters.ip_address,
            "session_id": currentFilters.session_id,
            "question": currentFilters.question,
            "answer": currentFilters.answer,
            "adjust_question": canViewAdjustQuestion ? currentFilters.adjust_question : "",
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            setWords(null);
            CreateWordCountListData(null, nextStartDate, nextEndDate);
            setAnalyticsFirstLoaded(true);
            dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
            setAnalyticsLoading(false);
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
        }
        else 
        {
            if (result.response.words && result.response.words.length > 0)
            {
                setWords(result.response.words)
            }
            else
            {
                setWords(null)
            }
            
            CreateWordCountListData(result.response.words, nextStartDate, nextEndDate);
            setAnalyticsFirstLoaded(true)
            setTimeout(() => {
                dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete))
                setAnalyticsLoading(false)
                // CreateChatLogListData([])
                // setIsListLoading(false)
            }, 1000);
        }
    };

    const AdminUserChatLogInfoApiRequestForCsvDownload = async (page, is_limit, nextStartDate, nextEndDate) => {

        // Check if dates are provided, fallback to state
        if (!nextStartDate || !nextEndDate) {
            nextStartDate = startDate;
            nextEndDate = endDate;
        }

        if (!nextStartDate || !nextEndDate) return;

        // リクエスト値の設定
        const range_start_year = nextStartDate.getFullYear();
        const range_start_month = nextStartDate.getMonth() + 1;
        const range_start_day = nextStartDate.getDate();
        const range_end_year = nextEndDate.getFullYear();
        const range_end_month = nextEndDate.getMonth() + 1;
        const range_end_day = nextEndDate.getDate();

        // 通信中の表示を出す
        dispatch(setLoading(true));
        const result = await ExecAdminUserChatLogInfoApi({
            "page": page,
            "is_limit": is_limit,
            "range_start_year": range_start_year,
            "range_start_month": range_start_month, 
            "range_start_day": range_start_day, 
            "range_end_year": range_end_year,
            "range_end_month": range_end_month, 
            "range_end_day": range_end_day,
            // ※この関数はoverrideFiltersを受け取らないため、フィルタ適用直後にCSVを落とすと
            //   1テンポ古いfilterParamsで出力される可能性がある（既存挙動）
            "ip_address": filterParams.ip_address,
            "session_id": filterParams.session_id,
            "question": filterParams.question,
            "answer": filterParams.answer,
            "adjust_question": canViewAdjustQuestion ? filterParams.adjust_question : "",
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
        }
        else 
        {
            dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
            const csvData = generateCSV(result.response.t_chat_log_list);
            // SJIS に変換して Blob を作成
            const sjisArray = Encoding.convert(csvData, { to: 'SJIS', type: 'array' });
            const uint8Array = new Uint8Array(sjisArray);
            const blob = new Blob([uint8Array], { type: 'text/csv;charset=Shift_JIS' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = Constants.CHAT_LOG_CSV_FILE_NAME;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    // CSVデータを生成する関数
    function generateCSV(data) {
        // ヘッダー行を定義（質問補強は閲覧権限があるときだけ列ごと出す）
        const headers = ["IP", "セッションID", "質問"];
        if (canViewAdjustQuestion) {
            headers.push("質問補強");
        }
        headers.push("返答", "日時");

        // CSV用の配列を作成し、ヘッダーを追加
        const csvRows = [headers.join(",")];

        // データ行を作成
        data.forEach(item => {
            const dateObject = new Date(item.regdate);
            const year = dateObject.getFullYear();
            const month = dateObject.getMonth() + 1; // 月は0から始まるため+1する
            const day = dateObject.getDate();
            const hours = String(dateObject.getHours()).padStart(2, '0');
            const minutes = String(dateObject.getMinutes()).padStart(2, '0');
            const seconds = String(dateObject.getSeconds()).padStart(2, '0');
            const row = [
                item.ip_address,
                item.session_id,
                `"${item.question}"`,  // カンマや特殊文字がある可能性があるため、ダブルクオートで囲む
            ];
            if (canViewAdjustQuestion) {
                row.push(`"${item.adjust_question || ""}"`);
            }
            row.push(
                `"${item.answer}"`,    // 同上
                `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`
            );
            csvRows.push(row.join(","));
        });

        // CSVのテキストを結合
        return csvRows.join("\n");
    }

    // 表示期間ラジオボタンの変更
    const ChangeDateRadio = (value) => {

        // 選択中のラジオボタンをセット
        setDateRadioCheck(value);
        // 日付選択をリセット
        setDispCustomDate(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM])

        // カスタムの場合
        if (value == Constants.RANGE_TYPE_CUSTOM)
        {
            return
        }

        const { nextStartDate, nextEndDate } = SetRangeByTargetDay(value)
        
        // API実行
        setAnalyticsLoading(true)
        AdminGetLogAnalyticsApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, nextStartDate, nextEndDate)
    };

    // 表示期間ラジオボタンのクリック
    const ClickDateRadio = (value) => {
        // カスタムの場合
        if (value == Constants.RANGE_TYPE_CUSTOM)
        {
            setIsDialogVisible(true)
        }
    };

    function CreateChatLogListData(responseTChatLogList)
    {
        // List用データの生成
        const listData = CreateChatLogListSchema(canViewAdjustQuestion)

        if (!responseTChatLogList)
        {
            setChatLogListData(listData);
            return
        }

        // 使用するデータ
        let tChatLogList;
        tChatLogList = Object.values(responseTChatLogList);

        if(tChatLogList.length > 0) {
            tChatLogList.forEach((tChatLog) => {
                // データの整形
                const tChatLogData = {};
                const titleData = {};
                // そのまま使えるデータを入れる
                tChatLogData.ip_address = tChatLog.ip_address + "\n" + tChatLog.session_id;
                // 質問 / 質問補強（1セル内に縦積み）
                // ※権限が無い、またはadjust_questionが空文字（use_adjust_question=0 / 共感のみフロー）の
                //   場合は質問だけを出し、区切り線も出さない
                const adjustQuestion = (canViewAdjustQuestion && tChatLog.adjust_question) ? tChatLog.adjust_question : "";
                tChatLogData.question = (
                    <Stack className="chatlog-question-cell width-full">
                        <div className="chatlog-question-text">{tChatLog.question}</div>
                        {adjustQuestion &&
                            <div className="chatlog-adjust-question">{adjustQuestion}</div>
                        }
                    </Stack>
                );
                // ホバーツールチップ用のプレーンテキスト（FitListがtitle属性に流し込む）
                titleData.question = adjustQuestion
                    ? `${tChatLog.question}\n\n補強: ${adjustQuestion}`
                    : tChatLog.question;
                tChatLogData.answer=tChatLog.answer;
                const dateObject = new Date(tChatLog.regdate);
                const year = dateObject.getFullYear();
                const month = dateObject.getMonth() + 1; // 月は0から始まるため+1する
                const day = dateObject.getDate();
                const hours = String(dateObject.getHours()).padStart(2, '0'); // 0埋め
                const minutes = String(dateObject.getMinutes()).padStart(2, '0'); // 0埋め
                const seconds = String(dateObject.getSeconds()).padStart(2, '0'); // 0埋め
                tChatLogData.regdate = `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`;

                // 各データのクリック時処理
                const onClick = () => {
                    
                };

                // リスト用データの作成
                listData.value.push({
                    data: tChatLogData,
                    title: titleData,
                    onclick: onClick
                });
            });
        }
        setChatLogListData(listData);
    }

    // valueが0～100の整数値とする
    const getColor = (value) => {
        // COLOR_DICTのキーを数値配列に変換し昇順ソート
        const keys = Object.keys(COLOR_DICT).map(Number).sort((a, b) => a - b);
        
        // valueより大きいキーが出るまでループ、valueより大きいキーがあれば一つ前のキーの色を返す
        let matchedKey = keys[0];
        for (let i = 0; i < keys.length; i++) {
        if (value < keys[i]) {
            break;
        }
        matchedKey = keys[i];
        }
        
        return COLOR_DICT[matchedKey];
    }

    function CreateWordCountListData(words, nextStartDate, nextEndDate)
    {
        // List用データの生成
        const listData = {
            columsize: {
                word:3, count:1,  graph:2
            },
            title: {
                word: "単語",
                count: "回数",
                graph: "",
            },
            value: []
        }

        if (!words || words.length === 0)
        {
            setWordCountData(listData);
            return
        }

        // 使用するデータ
        const maxWord = words.reduce((prev, current) =>
            current.value > prev.value ? current : prev
        );
        
        if(words.length > 0) {
            const  maxFrequency = (words ? Math.max(...words.map((word) => Number(word.value))) : 1)
            const  minFrequency = (words ? Math.min(...words.map((word) => Number(word.value))) : 0)
            words.forEach((word) => {

                // データの整形
                const wordData = {};
                const titleData = {};
                // そのまま使えるデータを入れる
                wordData.text = word.text;
                wordData.value = word.value;
                const rate = ( Number(word.value) / Number(maxWord.value) ) * 100;

                const frequency = ((Number(word.value) - minFrequency) / (maxFrequency - minFrequency)) * 100; // 最大値からの割合(%)を取得

                const colorHex = getColor(frequency)

                const [r, g, b] = colorHex.match(/\w\w/g).map((hex) => parseInt(hex, 16)); // RGB値を抽出
                const colorStyle = `rgba(${r}, ${g}, ${b}, ${1})`; // RGBA形式の色を返す

                wordData.graph = (
                    <div className="graph-element" style={{
                        width : `${rate}%`,
                        backgroundColor : colorStyle
                    }}></div>
                );

                // 各データのクリック時処理
                const onClick = () => {
                    handleChangeWord(word.text, true, nextStartDate, nextEndDate);
                };

                // リスト用データの作成
                listData.value.push({
                    data: wordData,
                    title: titleData,
                    onclick: onClick
                });
            });
        }
        setWordCountData(listData);
    }

    const handlePageChange = (page) => {
        AdminUserChatLogInfoApiRequest(page, DEFAULT_LIMIT_FLG, startDate, endDate)
    };

    const handleChangeWord = (word, refreshLogs = false, nextStartDate = null, nextEndDate = null) => {
        if (word === null) {
            return;
        }
        setFilterParams(prev => ({ ...prev, question: word }));
        if (refreshLogs)
        {
            if (analyticsFirstLoaded)
            {
                scrollToRef(listRef)
            }
            const start = nextStartDate !== null ? nextStartDate : startDate;
            const end = nextEndDate !== null ? nextEndDate : endDate;
            AdminUserChatLogInfoApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, start, end, true, true, { ...filterParams, question: word });
        }
    };

    const handleGetLogAnalytics = () => {
        setAnalyticsLoading(true)
        AdminGetLogAnalyticsApiRequest(currentPage, DEFAULT_ANALYTICS_LIMIT_FLG, startDate, endDate)
    };

    const csvDownload = () => {
        AdminUserChatLogInfoApiRequestForCsvDownload(DEFAULT_PAGE, 0, startDate, endDate)
    };

    // フィルターを適用する
    const applyFilters = () => {
        setIsFilterDrawerOpen(false);
        setFilterParams(editFilterParams);
        setAnalyticsLoading(true);
        // フィルター適用後、ログリストを更新
        AdminUserChatLogInfoApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, startDate, endDate, true, true, editFilterParams);
        // フィルター適用後、ワードクラウドを更新
        AdminGetLogAnalyticsApiRequest(DEFAULT_PAGE, DEFAULT_ANALYTICS_LIMIT_FLG, startDate, endDate, editFilterParams);
    };

    // ドロワーを閉じる（適用せずに閉じた場合は編集内容を巻き戻す）
    const cancelFilterChanges = () => {
        setIsFilterDrawerOpen(false);
        setEditFilterParams(filterParams); // 元のフィルターに戻す
        setFilterInputsKey(Date.now()); // 入力コンポーネントの内部状態もリセット
    };

    // フィルターを削除する
    const removeFilter = (key) => {
        const overrides = { ...filterParams };
        if (key === 'ip_address') {
            overrides.ip_address = "";
        }
        if (key === 'session_id') {
            overrides.session_id = "";
        }
        if (key === 'question') {
            overrides.question = "";
        }
        if (key === 'answer') {
            overrides.answer = "";
        }
        if (key === 'adjust_question') {
            overrides.adjust_question = "";
        }

        setFilterParams(overrides);
        setAnalyticsLoading(true);

        AdminUserChatLogInfoApiRequest(DEFAULT_PAGE, DEFAULT_LIMIT_FLG, startDate, endDate, true, true, overrides);
        AdminGetLogAnalyticsApiRequest(DEFAULT_PAGE, DEFAULT_ANALYTICS_LIMIT_FLG, startDate, endDate, overrides);
    };

    const listRef = useRef(null);
    const topRef = useRef(null);

    const scrollToRef = (targetRef) => {
        console.log(targetRef.current);
        if (targetRef.current) {
            console.log("scroll started");
            targetRef.current.scrollIntoView({
                behavior: "smooth", // スムーズなスクロール
                block: "start",    // スクロール位置（'start', 'center', 'end' など）
            });
        }
    };

    // 頻度ラベル → 並び順スコア (大きいほど上位)
    const FREQUENCY_ORDER = { "高": 3, "中": 2, "低": 1 };

    // LLMから返ってきたテキストをFAQ JSONとしてパースし、頻度高い順にソートする
    const parseFaqResponse = (text) => {
        if (!text) return [];
        let body = text.trim();
        const fenceMatch = body.match(/```(?:json)?\s*([\s\S]*?)```/i);
        if (fenceMatch) {
            body = fenceMatch[1].trim();
        }
        const firstBrace = body.indexOf("{");
        const lastBrace = body.lastIndexOf("}");
        if (firstBrace >= 0 && lastBrace > firstBrace) {
            body = body.slice(firstBrace, lastBrace + 1);
        }
        try {
            const data = JSON.parse(body);
            let list = [];
            if (Array.isArray(data.faq_list)) list = data.faq_list;
            else if (Array.isArray(data)) list = data;
            // 頻度高い順 (高→中→低→未定義) に並び替え
            return [...list].sort((a, b) => {
                const sa = FREQUENCY_ORDER[a && a.frequency_level] || 0;
                const sb = FREQUENCY_ORDER[b && b.frequency_level] || 0;
                return sb - sa;
            });
        } catch (e) {
            console.warn("FAQ parse failed:", e);
            return [];
        }
    };

    // 設定ポップアップのカレンダーを欄外クリックで閉じる (確定状態に巻き戻す)
    useEffect(() => {
        if (!faqConfigIsDialogVisible) return;
        const handleClickOutsideFaqCalendar = (event) => {
            // DateRadio の「カスタム」ラベルをクリックして開いた直後に拾わないよう、ダイアログ外のみ閉じる判定
            if (faqConfigDialogRef.current && !faqConfigDialogRef.current.contains(event.target)) {
                revertFaqConfigDates();
                setFaqConfigIsDialogVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideFaqCalendar);
        return () => document.removeEventListener('mousedown', handleClickOutsideFaqCalendar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [faqConfigIsDialogVisible]);

    // 日付範囲 → API用 params に変換
    const buildRangeParams = (start, end) => {
        const params = {
            range_start_year: 0, range_start_month: 0, range_start_day: 0,
            range_end_year: 0, range_end_month: 0, range_end_day: 0,
        };
        if (start && !isNaN(start.getTime())) {
            params.range_start_year = start.getFullYear();
            params.range_start_month = start.getMonth() + 1;
            params.range_start_day = start.getDate();
        }
        if (end && !isNaN(end.getTime())) {
            params.range_end_year = end.getFullYear();
            params.range_end_month = end.getMonth() + 1;
            params.range_end_day = end.getDate();
        }
        return params;
    };

    // 指定した期間の対話件数を取得 (設定ポップアップの件数表示用)
    const fetchFaqCount = async (start, end) => {
        setIsFaqCountLoading(true);
        const params = buildRangeParams(start, end);
        const { response } = await CallAdminLogAnalyticsFaqCountApi(params);
        setIsFaqCountLoading(false);
        if (response && response.error_code === 0) {
            setFaqCountInPeriod(response.chat_log_count || 0);
            if (response.limit) setFaqAnalysisLimit(response.limit);
        } else {
            setFaqCountInPeriod(0);
            closeFaqConfigModal();
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
    };

    // ラジオから期間を計算 (Constants.RANGE_TYPE_XXX → [start, end])
    const computeRangeFromRadio = (radioValue) => {
        const end = new Date();
        const start = new Date(end);
        start.setDate(end.getDate() - (Number(radioValue) - 1));
        return [start, end];
    };

    // ポップアップの日付ラジオを変更
    const changeFaqConfigDateRadio = (value) => {
        setFaqConfigDateRadio(value);
        setFaqConfigDispCustomDate(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
        if (Number(value) === Constants.RANGE_TYPE_CUSTOM) {
            return;
        }
        const [s, e] = computeRangeFromRadio(value);
        setFaqConfigStartDate(s);
        setFaqConfigEndDate(e);
        fetchFaqCount(s, e);
    };

    const clickFaqConfigDateRadio = (value) => {
        if (Number(value) === Constants.RANGE_TYPE_CUSTOM) {
            // カレンダーを開く前の確定状態をスナップショット (キャンセル/欄外クリックで巻き戻すため)
            faqConfigSnapshotRef.current = {
                start: faqConfigStartDate,
                end: faqConfigEndDate,
                disp: faqConfigDispCustomDate,
            };
            setFaqConfigIsDialogVisible(true);
        }
    };

    // カレンダー開時のスナップショットに状態を巻き戻す
    const revertFaqConfigDates = () => {
        const snap = faqConfigSnapshotRef.current;
        if (!snap) return;
        setFaqConfigStartDate(snap.start);
        setFaqConfigEndDate(snap.end);
        setFaqConfigDispCustomDate(snap.disp || Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
    };

    const handleFaqConfigDateChange = (update) => {
        setFaqConfigStartDate(update[0]);
        setFaqConfigEndDate(update[1]);
    };

    const handleFaqConfigCalendarCancel = () => {
        // キャンセル: カレンダー開時の確定状態に戻す
        revertFaqConfigDates();
        setFaqConfigIsDialogVisible(false);
    };

    const handleFaqConfigCalendarUpdate = () => {
        setFaqConfigIsDialogVisible(false);
        if (faqConfigStartDate && faqConfigEndDate) {
            const radioOpts = { month: 'long', day: 'numeric' };
            setFaqConfigDispCustomDate(
                faqConfigStartDate.toLocaleDateString('ja-JP', radioOpts) + "-" +
                faqConfigEndDate.toLocaleDateString('ja-JP', radioOpts)
            );
            fetchFaqCount(faqConfigStartDate, faqConfigEndDate);
        }
    };

    // 設定ポップアップを開く: 現在の画面表示期間を引き継ぐ
    const openFaqConfigModal = () => {
        const start = execApiStartDate ? new Date(execApiStartDate) : null;
        const end = execApiEndDate ? new Date(execApiEndDate) : null;

        // ラジオの初期値はチャットログ画面の選択を踏襲
        setFaqConfigDateRadio(dateRadioCheck);
        if (Number(dateRadioCheck) === Constants.RANGE_TYPE_CUSTOM && start && end) {
            const radioOpts = { month: 'long', day: 'numeric' };
            setFaqConfigDispCustomDate(
                start.toLocaleDateString('ja-JP', radioOpts) + "-" +
                end.toLocaleDateString('ja-JP', radioOpts)
            );
        } else {
            setFaqConfigDispCustomDate(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
        }
        setFaqConfigStartDate(start);
        setFaqConfigEndDate(end);
        setIsShowFaqConfigModal(true);
        if (start && end) {
            fetchFaqCount(start, end);
        } else {
            setFaqCountInPeriod(0);
        }
    };

    const closeFaqConfigModal = () => {
        setIsShowFaqConfigModal(false);
        setFaqConfigIsDialogVisible(false);
    };

    // 分析開始: 設定ポップアップを閉じて結果モーダルを開き、 API 実行
    const executeFaqAnalysis = () => {
        closeFaqConfigModal();
        setIsShowFaqModal(true);
        handleFaqAnalysis(faqConfigStartDate, faqConfigEndDate);
    };

    const handleFaqAnalysis = async (start, end) => {
        setIsFaqGenerating(true);
        setFaqRawText("");
        setFaqList([]);
        setFaqHtml("");
        setFaqInquiryCount(0);

        const params = buildRangeParams(start, end);
        const { response } = await CallAdminLogAnalyticsFaqApi(params);
        setIsFaqGenerating(false);

        if (response && response.error_code === 0) {
            const rawText = response.faq_text || "";
            setFaqRawText(rawText);
            const parsedList = parseFaqResponse(rawText);
            setFaqList(parsedList);
            if (parsedList.length === 0) {
                setFaqHtml(ConvertMarkdownToHTML(rawText));
            } else {
                setFaqHtml("");
            }
            setFaqInquiryCount(response.chat_log_count || 0);
        } else {
            setIsShowFaqModal(false);
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
    };

    // CSVセル用にエスケープ
    const toCsvCell = (value) => {
        const v = value === null || value === undefined ? "" : String(value);
        return `"${v.replace(/"/g, '""')}"`;
    };

    const buildFaqCsv = () => {
        const headers = ["番号", "質問", "カテゴリ", "頻度"];
        const rows = [headers.map(toCsvCell).join(",")];
        if (faqList && faqList.length > 0) {
            faqList.forEach((item, idx) => {
                const row = [
                    idx + 1,
                    item.question || "",
                    item.category || "",
                    item.frequency_level || "",
                ];
                rows.push(row.map(toCsvCell).join(","));
            });
        } else if (faqRawText) {
            rows.push([toCsvCell(""), toCsvCell(faqRawText), toCsvCell(""), toCsvCell("")].join(","));
        }
        return rows.join("\r\n");
    };

    const downloadFaqAsCsv = async () => {
        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await CallLogsCollectApi({
            event_name: "log_analyze_download"
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        const csvData = buildFaqCsv();
        if (!csvData) return;
        const d = new Date();
        const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
        const filename = `よくある質問_${stamp}.csv`;
        const sjisArray = Encoding.convert(csvData, { to: 'SJIS', type: 'array' });
        const uint8Array = new Uint8Array(sjisArray);
        const blob = new Blob([uint8Array], { type: 'text/csv;charset=Shift_JIS' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const closeFaqModal = () => {
        if (isFaqGenerating) return;
        setIsShowFaqModal(false);
        setFaqRawText("");
        setFaqList([]);
        setFaqHtml("");
        setFaqInquiryCount(0);
    };

    return (
    <PageContent pageId="AdminUserChatLog">
        <div ref={topRef}></div>
        <PageHeader>
            {/* ここにページ内ヘッダー部分のコンポーネントを記述 */}
            <Stack className="spacing-4px">
                <div className="page-header-title">ログ</div>
                <div className="page-header-detail">{Constants.AI_NAME}が応答したチャットのログを確認できます。</div>
            </Stack>
        </PageHeader>
        <PageWrapper>
            <PageContainer>
                <Stack>
                    <Card className="analytics-area base-card margin-bottom-16px">
                        <Stack className="spacing-32px">
                            <Row className="flex-both width-full">
                                <Stack className="analytics-title">
                                    <div className="text-size-medium text-bold">分析結果</div>
                                    <div className="text-size-small">問い合わせログの分析結果を表示します。</div>
                                </Stack>
                                <Stack className="width-280px spacing-8px align-center flex-bottom height-full">
                                    <Row className= "width-full align-center flex-both">
                                        <Stack className="flex-bottom height-full">
                                            対話実行回数
                                        </Stack>
                                        <Row className="flex-right height-full align-bottom">
                                            <div className="value-text text-size-large text-semibold height-fit">{tUserAdd ? (tUserAdd.talk_count).toLocaleString() : "--" }</div>
                                            <div className="value-text text-size-large text-semibold height-fit"></div>
                                            <div className="limit-text height-fit"> / {iConstantList ? maxTalkCount.toLocaleString() : "--"}</div>
                                        </Row>
                                    </Row>
                                    <ProgressBar progress={(tUserAdd ? (tUserAdd.talk_count / maxTalkCount) : 0 ) } subProgress={0}></ProgressBar>
                                </Stack>
                            </Row>
                            <div className="data-radio-stack">
                                <Row className= "row-stack-between spacing-32px">
                                    <Row className="flex-left date-radio-area">
                                        <DateRadio
                                            name="range-date"
                                            value={RangeLabelTypeKeys}
                                            diffValue={DispRangeLabelTypeValues}
                                            checked={dateRadioCheck}
                                            onclick={(e) => ClickDateRadio(e)}
                                            onchange={(e) => ChangeDateRadio(e)}
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                        />
                                        {isDialogVisible && (
                                            <div className="calender-dialog" ref={dialogRef}>
                                                <CustomDateRangePicker
                                                    selected={startDate}
                                                    onChange={handleDateChange}
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    selectsRange
                                                    inline
                                                    handleCancel={handleCancel}
                                                    handleUpdate={handleUpdate}
                                                    minDate ={minDate}
                                                    maxDate ={maxDate}
                                                    isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                                />
                                            </div>
                                        )}
                                    </Row>
                                    
                                    <Button className={"primary faq-analysis-button width-150px" + (isUpdateLocked ? " disabled" : "")} disabled={isUpdateLocked} onClick={openFaqConfigModal}>
                                        よくある質問分析
                                    </Button>
                                    <Row className="flex-right spacing-8px width-full">
                                        {/* <Button className={"dl-button "+(true ? "primary": "primary disabled")} onClick={() => {
                                            handleGetLogAnalytics();
                                        }}>
                                            <Row className="flex-center spacing-8px width-full">
                                                <Icon className="label-icon" icon="graph-up" size={16} />
                                                <div>分析</div>
                                            </Row>
                                        </Button> */}
                                        <Row className="search-input-area width-full relative flex-right align-center spacing-8px">
                                            {filterParams.question && <FilterLabel label={`質問: ${filterParams.question}`} onClose={() => removeFilter('question')} />}
                                            {canViewAdjustQuestion && filterParams.adjust_question && <FilterLabel label={`質問補強: ${filterParams.adjust_question}`} onClose={() => removeFilter('adjust_question')} />}
                                            {filterParams.answer && <FilterLabel label={`回答: ${filterParams.answer}`} onClose={() => removeFilter('answer')} />}
                                            {filterParams.session_id && <FilterLabel label={`SessionID: ${filterParams.session_id}`} onClose={() => removeFilter('session_id')} />}
                                            {filterParams.ip_address && <FilterLabel label={`IPアドレス: ${filterParams.ip_address}`} onClose={() => removeFilter('ip_address')} />}
                                            <Button className="filter-button primary" onClick={() => {
                                                setEditFilterParams(filterParams);
                                                setFilterInputsKey(Date.now());
                                                setIsFilterDrawerOpen(true);
                                            }}>
                                                <Row className="flex-center spacing-8px width-fit">
                                                    <Icon className="label-icon" icon="filter" size={16} />
                                                    <div>フィルター</div>
                                                </Row>
                                            </Button>
                                        </Row>
                                    </Row>
                                </Row>
                            </div>
                            <div className={"filter-drawer-overlay " + (isFilterDrawerOpen ? "show" : "")} onClick={cancelFilterChanges}></div>
                            <div className={"filter-drawer " + (isFilterDrawerOpen ? "show" : "")}>
                                <Stack className="height-full spacing-16px">
                                    <Row className="flex-both align-center">
                                        <div className="text-size-medium text-bold">フィルター</div>
                                        <Row className="flex-right align-center spacing-8px">
                                            <Button className="secondary-border width-120px" onClick={() => {
                                                setEditFilterParams({
                                                    ip_address: "",
                                                    session_id: "",
                                                    question: "",
                                                    answer: "",
                                                    adjust_question: ""
                                                });
                                                setFilterInputsKey(Date.now());
                                            }}>
                                                クリア
                                            </Button>
                                            <Button className="primary width-120px" onClick={applyFilters}>
                                                適用
                                            </Button>
                                        </Row>
                                    </Row>
                                    <Stack className="spacing-8px flex-grow scroll-y">
                                        <Stack className="spacing-4px">
                                            <div className="text-size-small text-bold">IPアドレス</div>
                                            <TextInput key={`ip-${filterInputsKey}`} default={editFilterParams.ip_address} value={editFilterParams.ip_address} onchange={(e) => setEditFilterParams({...editFilterParams, ip_address: e.target.value})} placeholder="IPアドレスを入力" />
                                        </Stack>
                                        <Stack className="spacing-4px">
                                            <div className="text-size-small text-bold">セッションID</div>
                                            <TextInput key={`session-${filterInputsKey}`} default={editFilterParams.session_id} value={editFilterParams.session_id} onchange={(e) => setEditFilterParams({...editFilterParams, session_id: e.target.value})} placeholder="セッションIDを入力" />
                                        </Stack>
                                        <Stack className="spacing-4px">
                                            <div className="text-size-small text-bold">質問</div>
                                            <TextInput key={`q-${filterInputsKey}`} default={editFilterParams.question} value={editFilterParams.question} onchange={(e) => setEditFilterParams({...editFilterParams, question: e.target.value})} placeholder="質問内のキーワード" />
                                        </Stack>
                                        {canViewAdjustQuestion &&
                                            <Stack className="spacing-4px">
                                                <div className="text-size-small text-bold">質問補強</div>
                                                <TextInput key={`aq-${filterInputsKey}`} default={editFilterParams.adjust_question} value={editFilterParams.adjust_question} onchange={(e) => setEditFilterParams({...editFilterParams, adjust_question: e.target.value})} placeholder="質問補強内のキーワード" />
                                            </Stack>
                                        }
                                        <Stack className="spacing-4px">
                                            <div className="text-size-small text-bold">回答</div>
                                            <TextInput key={`a-${filterInputsKey}`} default={editFilterParams.answer} value={editFilterParams.answer} onchange={(e) => setEditFilterParams({...editFilterParams, answer: e.target.value})} placeholder="回答内のキーワード" />
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </div>
                            <Row className={"analytics-area-row spacing-8px" + ((words || analyticsLoading) ? " show" : "")} >
                                <Stack className="wordcloud-area width-50percent height-full spacing-8px">
                                    {/* <Row className="flex-center text-size-light-medium">ワードクラウド</Row> */}
                                    <WordCloudView
                                        className="wordcloud-area" 
                                        words={words}
                                        width={440}
                                        height={280}
                                        onWordClick={(word) => {
                                            handleChangeWord(word, true);
                                        }}
                                        colorDict={COLOR_DICT}
                                    ></WordCloudView>
                                </Stack>
                                <Stack className="ranking-area width-50percent spacing-8px">
                                    {/* <Row className="flex-center text-size-light-medium">ワードランキング</Row> */}
                                    <List
                                        list={wordCountData}
                                        header={true}
                                        scrollbar={"scrollbar"}
                                        className="ranking-list"
                                        isLoading={((! words) || analyticsLoading) ? true : false}
                                        loadingCount={0}
                                    />
                                </Stack>
                                {(analyticsLoading || !analyticsFirstLoaded) &&
                                    <Stack className="analytics-loading flex-center align-center">
                                        <div className="loading-spinner">
                                            <div className="loader"></div>
                                        </div>
                                    </Stack>
                                }
                                {(analyticsFirstLoaded && !analyticsLoading && !words) &&
                                    <Stack className="empty-info flex-center align-center">
                                        <InfoCard className="danger width-full">表示するデータがありません。</InfoCard>
                                    </Stack>
                                }
                            </Row>
                        </Stack>
                    </Card>
                    <div ref={listRef}></div>
                    <Card className = "base-card">
                        <Row className="flex-both width-full">
                            <Stack className="analytics-title">
                                <div className="text-size-medium text-bold">ログ詳細</div>
                                <div className="text-size-small">問い合わせログの詳細を表示します。</div>
                            </Stack>
                            <div className="data-radio-stack">
                                <Row className= "row-stack-between">
                                    <Row className="flex-right spacing-8px width-full">
                                        <Button className={"dl-button "+(isDownloadCsv ? "primary": "primary disabled")} onClick={csvDownload}>
                                            <Row className="flex-center spacing-8px width-full">
                                                <Icon className="label-icon" icon="download" size={18} />
                                                <div>ダウンロード</div>
                                            </Row>
                                        </Button>
                                    </Row>
                                </Row>
                            </div>
                        </Row>
                        <Stack className="spacing-16px align-center addmergin">
                            <FitList
                                list={chatLogListData}
                                header={true}
                                scrollbar={"scrollbar"}
                                isLoading={(isListLoading || (initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)) ? true : false}
                            />
                            <Pagination
                                totalPages={totalPages}
                                currentPage={currentPage}
                                onChange = {handlePageChange}
                                isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                            />
                        </Stack>
                    </Card>
                </Stack>
                <Button className="scroll-button primary" onClick={() => {scrollToRef(topRef)}}>
                    <Icon icon="chevron-up" size={28} className="copy-icon"/>
                </Button>
            </PageContainer>
        </PageWrapper>

        {/* よくある質問分析 - 設定ポップアップ (分析開始前) */}
        {isShowFaqConfigModal && (
            <div className="modal-overlay">
                <div className="modal-content faq-config-modal">
                    <div className="modal-header">
                        <div className="text-size-h2">よくある質問</div>
                    </div>
                    <div className="modal-body faq-config-body">
                        <Card className="faq-config-card">
                            <Row className="flex-both align-top width-full faq-config-top-row spacing-16px">
                                <Row className="flex-left date-radio-area faq-config-radio-area">
                                    <DateRadio
                                        name="faq-config-range-date"
                                        value={RangeLabelTypeKeys}
                                        diffValue={[...Object.values(Constants.RangeLabelType).filter(v => v !== Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]), faqConfigDispCustomDate]}
                                        checked={faqConfigDateRadio}
                                        onclick={(e) => clickFaqConfigDateRadio(e)}
                                        onchange={(e) => changeFaqConfigDateRadio(e)}
                                    />
                                    {faqConfigIsDialogVisible && (
                                        <div className="calender-dialog" ref={faqConfigDialogRef}>
                                            <CustomDateRangePicker
                                                selected={faqConfigStartDate}
                                                onChange={handleFaqConfigDateChange}
                                                startDate={faqConfigStartDate}
                                                endDate={faqConfigEndDate}
                                                selectsRange
                                                inline
                                                handleCancel={handleFaqConfigCalendarCancel}
                                                handleUpdate={handleFaqConfigCalendarUpdate}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                            />
                                        </div>
                                    )}
                                </Row>
                                <Stack className="width-280px spacing-8px align-center flex-bottom height-full">
                                    <Row className= "width-full align-center flex-both">
                                        <Stack className="flex-bottom height-full">
                                            選択期間中の対話実行回数
                                        </Stack>
                                        <Row className="flex-right height-full align-bottom">
                                            <div className="value-text text-size-large text-semibold height-fit">{isFaqCountLoading ? "-" : faqCountInPeriod.toLocaleString()}</div>
                                            <div className="value-text text-size-large text-semibold height-fit"></div>
                                            <div className="limit-text height-fit"> / {faqAnalysisLimit.toLocaleString()}</div>
                                        </Row>
                                    </Row>
                                    <ProgressBar progress={Math.min(faqCountInPeriod / Math.max(faqAnalysisLimit, 1), 1)} subProgress={0} />
                                </Stack>
                            </Row>
                            <InfoCard style={{ background: "none" }}>
                                <div>
                                    一度に分析できる質問は{faqAnalysisLimit.toLocaleString()}件までです。上限を超えるものは古いものから優先的に除外されます。
                                </div>
                            </InfoCard>
                        </Card>
                    </div>
                    <div className="modal-footer">
                        <Button className="secondary-border" onClick={closeFaqConfigModal}>
                            キャンセル
                        </Button>
                        <Button
                            className={"primary " + (!isUpdateLocked && faqCountInPeriod > 0 ? "" : "disabled")}
                            onClick={executeFaqAnalysis}
                            disabled={isUpdateLocked || faqCountInPeriod === 0 || isFaqCountLoading}
                        >
                            分析開始
                        </Button>
                    </div>
                </div>
            </div>
        )}

        {/* よくある質問分析モーダル */}
        {isShowFaqModal && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <div className="text-size-h2">よくある質問</div>
                    </div>
                    {isFaqGenerating ? (
                        <Stack className="faq-generating flex-center align-center spacing-16px">
                            <div className="faq-loading-badge primary">
                                <LoadingBox />
                            </div>
                            <div className="text-size-medium">分析中</div>
                            <div className="text-size-small text-color-weak">対話ログから「よくある質問」を分析しています...</div>
                        </Stack>
                    ) : (
                        <>
                            <div className="modal-body faq-body">
                                {faqList && faqList.length > 0 ? (
                                    <Stack className="spacing-16px">
                                        {faqList.map((item, index) => (
                                            <Card key={index} className="faq-card">
                                                <Row className="faq-card-meta flex-left align-center spacing-8px">
                                                    {item.frequency_level && (
                                                        <span className={"faq-badge faq-badge-frequency level-" + item.frequency_level}>
                                                            頻度: {item.frequency_level}
                                                        </span>
                                                    )}
                                                    {item.category && (
                                                        <span className="faq-badge faq-badge-category">{item.category}</span>
                                                    )}
                                                </Row>
                                                <Row className="faq-card-question align-top spacing-8px">
                                                    <div className="faq-marker question">Q</div>
                                                    <div className="faq-card-question-text">{item.question || ""}</div>
                                                </Row>
                                            </Card>
                                        ))}
                                    </Stack>
                                ) : (
                                    <div className="markdown-preview-card">
                                        <div className="markdown-content" dangerouslySetInnerHTML={{ __html: faqHtml }} />
                                    </div>
                                )}
                            </div>
                            <Stack className="background-gray align-center width-full flex-center spacing-16px">
                                <InfoCard style={{background: "none"}}>
                                    <div>この画面を閉じると結果は消去されます。必要な場合は内容をCSVで保存してください。</div>
                                    {faqInquiryCount > 0 && (
                                        <div>対象期間内の対話件数: {faqInquiryCount}件</div>
                                    )}
                                </InfoCard>
                            </Stack>
                            <div className="modal-footer">
                                <Button className="secondary-border" onClick={downloadFaqAsCsv} disabled={!faqRawText}>
                                    <Row className="flex-center spacing-8px">
                                        <Icon icon="download" size={20} />
                                        <div>CSVで保存</div>
                                    </Row>
                                </Button>
                                <Button className="primary" onClick={closeFaqModal}>
                                    閉じる
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )}
    </PageContent>
    );
};

export default AdminUserChatLog;